// @ts-strict-ignore
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ValidatorFactory } from '@shared/validator.factory';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BusbarArticleWizardPresenter } from '@project/components/article/presenter/busbar-article-wizard-presenter';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CustomValidators } from '@shared/custom-validators';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { BusbarArticleConnectProcessor } from '../processor/prepare/connect/busbar-article-connect.processor';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UnusedLabelingService } from '../../label/label-editor/service/unused-labeling.service';
import { ArticleMoveManager } from '../manager/article-move.manager';
import { CarrierBlockManager } from '../manager/carrier-block.manager';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ArticleCreationManagerFactory } from '../manager/article-creation.manager.factory';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';

@Component({
  standalone: false,
  selector: 'app-edit-n-busbar',
  templateUrl: './edit-n-busbar.component.html',
})
export class EditNBusbarComponent implements OnInit, OnDestroy {
  editNBusbar: FormGroup;

  public busbarArticleUsage;
  public maxtLengthBusbarProject: number = 0;
  public busbarArticleWizardPresenter: BusbarArticleWizardPresenter;

  public currentLengthBusbarProject: string;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private localeResourceManager: LocaleResourceManager,
    private articleManager: ArticleManager,
    private selectionModelService: SelectionModelService,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private projectMetaElementService: ProjectMetaElementService,
    private busbarArticleConnectProcessor: BusbarArticleConnectProcessor,
    private undoRedoManager: UndoRedoManager,
    private articleLineHolder: ArticleLineHolder,
    private undoRedoUtils: UndoRedoUtils,
    private containerService: ContainerService,
    private carrierBlockManager: CarrierBlockManager,
    private articleMoveManager: ArticleMoveManager,
    private layerManager: LayerManager,
    private projectContentPresenter: ProjectContentPresenter,
    private unusedLabelingService: UnusedLabelingService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private insertArticleUtils: InsertArticleUtils,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService,
    private validatorFactory: ValidatorFactory,
  ) {}

  ngOnInit() {
    this.busbarArticleWizardPresenter = this.getBusbarArticleWizardPresenter();
    this.busbarArticleWizardPresenter.busbarArticleUsage = this.busbarArticleUsage;
    this.maxtLengthBusbarProject = this.localeResourceManager.parseNumber(
      this.getLabelTextByValueInMillimeters(this.busbarArticleWizardPresenter.busbarMaxLength)
    );
    this.editNBusbar = this.formBuilder.group(
      {
        additionalStartLengthInput: [
          this.getLabelTextByValueInMillimeters(this.busbarArticleWizardPresenter.startOffsetLength),
          [
            CustomValidators.trimRequired,
            this.validatorFactory.number({min: this.busbarArticleWizardPresenter.minStartLengthInMillimeters}),
          ],
        ],
        lengthInput: [this.getLabelTextByValueInMillimeters(this.busbarArticleWizardPresenter.busbarLength)],
        additionalEndLengthInput: [
          this.getLabelTextByValueInMillimeters(this.busbarArticleWizardPresenter.endOffsetLength),
          [
            CustomValidators.trimRequired,
            this.validatorFactory.number({min: this.busbarArticleWizardPresenter.minEndLengthInMillimeters}),
          ],
        ],
        busbarMaxLengthInput: [this.maxtLengthBusbarProject],
        distanceToFirstArticleLabel: [this.getLabelTextByValueInMillimeters(this.busbarArticleWizardPresenter.distanceFromStartToFirstArticle)],
        distanceToLastArticleLabel: [this.getLabelTextByValueInMillimeters(this.busbarArticleWizardPresenter.distanceFromStartToLastArticle)],
      },
      {
        validator: (group) => this.lengthValidator(group),
      }
    );
  }

  ngOnDestroy() {}

  public async submit(): Promise<void> {
    if (!this.editNBusbar.valid) {
      return;
    }
    const startLength: number = this.busbarArticleWizardPresenter.millimetersToAway3DUnits(this.localeResourceManager.parseNumber(this.editNBusbar.controls['additionalStartLengthInput'].value));
    const endLength: number = this.busbarArticleWizardPresenter.millimetersToAway3DUnits(this.localeResourceManager.parseNumber(this.editNBusbar.controls['additionalEndLengthInput'].value));
    this.close();
    await this.busbarArticleWizardPresenter.startLoadingBusbar(startLength, endLength);
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public calculateProjectLength(group: FormGroup): void {
    const totalLength: number = this.localeResourceManager.parseNumber(group.controls['lengthInput'].value);
    const additionalStartLength: number = this.localeResourceManager.parseNumber(group.controls['additionalStartLengthInput'].value);
    const additionalEndLength: number = this.localeResourceManager.parseNumber(group.controls['additionalEndLengthInput'].value);
    this.currentLengthBusbarProject = this.localeResourceManager.formatNumber(totalLength + additionalStartLength + additionalEndLength);
  }

  public getLabelTextByValueInMillimeters(value: number, roundValue: boolean = true): string {
    const valueInMillimeters: number = roundValue ? this.getRoundedValueInMillimeters(value) : this.getValueInMillimeters(value);
    return this.localeResourceManager.formatNumber(valueInMillimeters, false);
  }

  private getRoundedValueInMillimeters(value: number): number {
    return this.getRoundedValue(this.getValueInMillimeters(value));
  }

  private getValueInMillimeters(value: number): number {
    return UnitConverter.fromTo('3d', 'mm', value);
  }

  public getRoundedValue(value: number): number {
    return MathUtils.roundNumberValue(
      value,
      BusbarArticleWizardPresenter.MM_DISPLAY_FRACTION_DIGITS,
      BusbarArticleWizardPresenter.MM_DISPLAY_ROUNDING_THRESHOLD
    );
  }

  private lengthValidator(group: FormGroup) {
    this.calculateProjectLength(group);
    return this.maxtLengthBusbarProject < this.localeResourceManager.parseNumber(this.currentLengthBusbarProject)
      ? {
          messageKey: 'validation.error.exceeds_max',
          param: this.maxtLengthBusbarProject,
        }
      : null;
  }

  private getBusbarArticleWizardPresenter(): BusbarArticleWizardPresenter {
    return new BusbarArticleWizardPresenter(
      this.articleCreationManagerFactory,
      this.articleLineHolder,
      this.articleManager,
      this.selectionModelService,
      this.projectModel,
      this.projectArticleService,
      this.projectMetaElementService,
      this.busbarArticleConnectProcessor,
      this.undoRedoManager,
      this.undoRedoUtils,
      this.containerService,
      this.carrierBlockManager,
      this.articleMoveManager,
      this.layerManager,
      this.projectContentPresenter,
      this.unusedLabelingService,
      this.insertArticleHelperFactory,
      this.insertArticleUtils,
      this.applicationRefreshService,
      this.detachArticleUsageService
    );
  }
}
